import type { ComponentStyleConfig } from '@chakra-ui/theme';
import { rem } from '../../utils/utils';
import { AvatarProps, AvatarSizes, AvatarVariant } from './Avatar';

const COLORS = [
  {
    backgroundColor: 'sys/neutral/subtle',
    color: 'sys/neutral/strong',
  },
  {
    backgroundColor: 'sys/success/subtle',
    color: 'sys/success/strong',
  },
  {
    backgroundColor: 'sys/warning/subtle',
    color: 'sys/warning/strong',
  },
  {
    backgroundColor: 'sys/orange/subtle',
    color: 'sys/orange/strong',
  },
  {
    backgroundColor: 'sys/info/subtle',
    color: 'sys/info/strong',
  },
  {
    backgroundColor: 'sys/critical/subtle',
    color: 'sys/critical/strong',
  },
  {
    backgroundColor: 'sys/interactive/subtle',
    color: 'sys/interactive/strong',
  },
  {
    backgroundColor: 'sys/turquoise/subtle',
    color: 'sys/turquoise/strong',
  },
];

const getStyleForUserVariant = (size: AvatarSizes) => {
  const borderRadius = {
    24: '12',
    32: '16',
    40: '20',
    48: '24',
    64: '12',
    128: '24',
  };

  return {
    borderRadius: borderRadius[size],
  };
};

const getStyleForWorkspaceVariant = (size: AvatarSizes) => {
  const borderRadius = {
    24: '4',
    32: '8',
    40: '8',
    48: '12',
    64: '12',
    128: '24',
  };

  return {
    borderRadius: borderRadius[size],
  };
};

const getStyleForBrandVariant = (size: AvatarSizes) => {
  const style = {
    24: {
      borderRadius: '4',
      svg: {
        height: '16',
        width: '16',
      },
    },
    32: {
      borderRadius: '8',
      svg: {
        height: '20',
        width: '20',
      },
    },
    40: {
      borderRadius: '8',
      svg: {
        height: '24',
        width: '24',
      },
    },
    48: {
      borderRadius: '12',
      svg: {
        height: '32',
        width: '32',
      },
    },
    64: {
      borderRadius: '12',
      svg: {
        height: '48',
        width: '48',
      },
    },
    128: {
      borderRadius: '12',
      svg: {
        height: '96',
        width: '96',
      },
    },
  };

  return {
    ...style[size],
  };
};

const getStyleForLettersVariant = (size: AvatarSizes) => {
  const borderRadius = {
    24: '4',
    32: '8',
    40: '8',
    48: '12',
    64: '12',
    128: '12',
  };

  return {
    borderRadius: borderRadius[size],
    padding: size === '128' ? '16' : undefined,
  };
};

const getStyleForProjectVariant = (size: AvatarSizes) => {
  const borderRadius = {
    24: '8',
    32: '8',
    40: '8',
    48: '12',
    64: '12',
    128: '24',
  };

  return {
    borderRadius: borderRadius[size],
  };
};

const getStyleForStepVariant = (size: AvatarSizes) => {
  const borderRadius = {
    24: '4',
    32: '4',
    40: '4',
    48: '4',
    64: '8',
    128: '12',
  };

  return {
    borderRadius: borderRadius[size],
  };
};

const getSizeBasedTextStyle = (size: AvatarSizes) => {
  const textStyles = {
    24: 'sans/size-1/bold',
    32: 'sans/size-3/bold',
    40: 'sans/size-4/bold',
    48: 'sans/size-5/semibold',
    64: 'sans/size-6/semibold',
    128: 'sans/size-8/semibold',
  };

  return {
    textStyle: textStyles[size],
    fontWeight: Number(size) > 40 ? 'semibold' : 'bold',
  };
};

const getStyleBySizeAndType = (size: AvatarSizes, variant: AvatarVariant) => {
  switch (variant) {
    case 'user':
      return {
        ...getStyleForUserVariant(size),
      };
    case 'workspace':
      return {
        ...getStyleForWorkspaceVariant(size),
      };
    case 'brand':
      return {
        ...getStyleForBrandVariant(size),
      };
    case 'letters':
      return {
        ...getStyleForLettersVariant(size),
      };
    case 'project':
      return {
        ...getStyleForProjectVariant(size),
      };
    case 'step':
      return {
        ...getStyleForStepVariant(size),
      };
  }
};

const getColors = ({ name, variant }: AvatarProps) => {
  if (name) {
    const str = name.toUpperCase();
    const seed = str.charCodeAt(0) + str.charCodeAt(str.length - 1);
    return COLORS[seed % COLORS.length];
  }
  if (variant === 'brand') {
    return { backgroundColor: 'sys/neutral/subtle', color: 'text/secondary' };
  }
  return {};
};

const AvatarTheme: ComponentStyleConfig = {
  baseStyle: ({ name, size, variant }) => {
    return {
      container: {
        ...getColors({ name, variant }),
        overflow: 'hidden',
        height: rem(size),
        width: rem(size),
        textTransform: 'capitalize',
        ...getSizeBasedTextStyle(size),
        ...getStyleBySizeAndType(size, variant),
      },
    };
  },
  parts: ['container'],
};

export default AvatarTheme;
